// Sampled SFX engine. Everything the player hears is now a real recorded CC0
// sample (Kenney — see public-v2/sfx/SOURCES.txt); only the three tape-transport
// cues are still synthesized, because they underscore a transition rather than
// punctuate a moment.
//
// COMBAT AUDIO HISTORY — read before changing the numbers below.
// The battle layer has been rejected by playtesters three times, each time while
// procedural:
//   1. "sounds like a cacophony"  -> added throttling + a voice cap
//   2. "reads as a phaser zap"    -> swapped highpass cracks for lowpassed bodies
//   3. universally rejected       -> gave up on synthesis for combat entirely
// Synthesized impacts kept landing somewhere between chiptune and sine beep no
// matter how they were shaped. attack/hit/death are now Kenney punch/cloth/body
// samples, and the anti-repetition mechanism is ROUND-ROBIN VARIANTS rather than
// pitch jitter on one fixed source — several genuinely different takes beats one
// take detuned, which is what made repeats grate.
//
// Gain pipeline: sample -> per-cue trim (CUE_TRIM) -> dynamic gain
// (audioMaster x audioSfx, perceptually eased) -> masterGain -> out.
//
// LOUDNESS. Two independent stages, and it matters which one you reach for:
//   - the FILES are loudness-matched at build time by tools/build-combat-sfx.py
//     (RMS-normalised per family, limited, high-passed). Do not re-level a file
//     to make one cue quieter.
//   - the MIX is CUE_TRIM below. Combat sits far under the UI layer on purpose:
//     an auto-battler fires attack+hit on every exchange, so those two cues are
//     heard hundreds of times per session and are the entire fatigue budget.
// Net effect vs the procedural set: attack ~7 dB quieter, hit ~8 dB quieter.

import { useAccountStore } from '../stores/account';

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let resumeWired = false;

// Voice + throttle bookkeeping
const VOICE_CAP = 6;
let voiceCount = 0;
const lastFireByKey: Record<string, number> = {};
// Minimum gap between two fires in the same bucket; a fire inside the gap is
// DROPPED, not queued. Widened when combat moved from synthesis to samples: the
// samples are longer than the graphs they replaced (hit 0.26s and hitbig 0.34s vs
// ~0.15s procedural), so the old gaps let three or four tails pile up on top of
// each other, which is precisely the overlap that got called a cacophony. Each gap
// is now a little over half the length of the sample it gates.
const THROTTLE_MS: Record<string, number> = {
  attack: 120,
  hit: 150,
  death: 220,
};

/** Per-call jitter — ±jitterPct (default 5%) breaks up repetition. */
function jitter(value: number, pct = 0.05): number {
  return value * (1 + (Math.random() - 0.5) * 2 * pct);
}

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    masterGain = ctx.createGain();
    masterGain.gain.value = 1;
    masterGain.connect(ctx.destination);
    if (!resumeWired) {
      const resume = () => {
        ctx?.resume().catch(() => {});
      };
      window.addEventListener('pointerdown', resume, { passive: true });
      window.addEventListener('keydown', resume);
      resumeWired = true;
    }
    preloadSamples();
    return ctx;
  } catch {
    return null;
  }
}

function effectiveSfxGain(): number {
  const { audioMaster, audioSfx } = useAccountStore.getState();
  const linear = (audioMaster / 100) * (audioSfx / 100);
  // Perceptual easing — a 50% slider should feel like half-loudness, not -6 dB.
  return linear * linear;
}

type EnvelopeShape = {
  attack: number;
  decay: number;
  sustain: number;
  release: number;
  peak?: number;
};

function envelope(
  c: AudioContext,
  env: EnvelopeShape,
  startTime: number,
  duration: number,
): GainNode {
  const g = c.createGain();
  const peak = env.peak ?? 1;
  const t0 = startTime;
  const t1 = t0 + env.attack;
  const t2 = t1 + env.decay;
  const t3 = t0 + duration;
  const sustainTime = Math.max(t2, t3 - env.release);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(peak, t1);
  g.gain.linearRampToValueAtTime(peak * env.sustain, t2);
  g.gain.setValueAtTime(peak * env.sustain, sustainTime);
  g.gain.linearRampToValueAtTime(0, t3);
  return g;
}

type FreqSpec = number | { from: number; to: number; curve?: 'lin' | 'exp' };

function osc(
  c: AudioContext,
  type: OscillatorType,
  freq: FreqSpec,
  startTime: number,
  duration: number,
): OscillatorNode {
  const o = c.createOscillator();
  o.type = type;
  if (typeof freq === 'number') {
    o.frequency.setValueAtTime(freq, startTime);
  } else {
    o.frequency.setValueAtTime(freq.from, startTime);
    if (freq.curve === 'exp') {
      o.frequency.exponentialRampToValueAtTime(
        Math.max(0.0001, freq.to),
        startTime + duration,
      );
    } else {
      o.frequency.linearRampToValueAtTime(freq.to, startTime + duration);
    }
  }
  o.start(startTime);
  o.stop(startTime + duration + 0.02);
  return o;
}

function noiseBuffer(c: AudioContext, durationSec: number): AudioBufferSourceNode {
  const length = Math.max(1, Math.ceil(c.sampleRate * durationSec));
  const buf = c.createBuffer(1, length, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  return src;
}

/**
 * Build and play a synth graph.
 *
 * @param key throttle bucket — sounds in the same bucket can't fire
 *            within THROTTLE_MS[key] of each other; omit to skip throttle
 *            (used by priority cues like victory/defeat).
 * @param durationMs hint for voice-counter release; over-estimate is fine.
 * @param build graph constructor receiving (ctx, destination, startTime).
 */
function playGraph(
  key: string | null,
  durationMs: number,
  build: (c: AudioContext, dest: AudioNode, t0: number) => void,
  trimKey?: string,
) {
  // Throttle gate — drop if too soon after a same-key fire.
  if (key) {
    const min = THROTTLE_MS[key] ?? 0;
    if (min > 0) {
      const now = performance.now();
      if ((lastFireByKey[key] ?? 0) + min > now) return;
      lastFireByKey[key] = now;
    }
    // Voice-cap drop — only non-priority sounds get dropped.
    if (voiceCount >= VOICE_CAP) return;
  }

  const c = getCtx();
  if (!c || !masterGain) return;
  // Same CUE_TRIM table as the sampled path, so the mix is tuned in one place
  // regardless of whether a cue is synthesized or a sample.
  const gain = effectiveSfxGain() * (CUE_TRIM[trimKey ?? key ?? ''] ?? 1);
  if (gain <= 0.001) return;

  // Slight gain jitter for variation; clamped to avoid silent fires.
  const dyn = c.createGain();
  dyn.gain.value = Math.max(0.01, jitter(gain, 0.1));
  dyn.connect(masterGain);

  build(c, dyn, c.currentTime + 0.005);

  // Voice tracking — bump now, drop after the graph's longest envelope.
  voiceCount++;
  window.setTimeout(() => {
    voiceCount = Math.max(0, voiceCount - 1);
  }, durationMs + 80);
}

// ---------------------------------------------------------------------------
// Sampled playback — tavern/shop/UI layer. Files + sourcing are documented
// in public-v2/sfx/SOURCES.txt (Kenney, CC0).
// ---------------------------------------------------------------------------

const SAMPLE_FILE: Record<string, string> = {
  click: 'click.ogg',
  error: 'error.ogg',
  success: 'confirm.ogg',
  buy: 'buy.ogg',
  refresh: 'shuffle.ogg',
  tierUp: 'tier-up.ogg',
  heroPower: 'hero-power.ogg',
  triple: 'triple.ogg',
  victory: 'victory.ogg',
  defeat: 'defeat.ogg',
  // Combat — multiple takes per cue, picked round-robin. See COMBAT_VARIANTS.
  'atk-1': 'atk-1.ogg',
  'atk-2': 'atk-2.ogg',
  'atk-3': 'atk-3.ogg',
  'hit-1': 'hit-1.ogg',
  'hit-2': 'hit-2.ogg',
  'hit-3': 'hit-3.ogg',
  'hit-4': 'hit-4.ogg',
  'hitbig-1': 'hitbig-1.ogg',
  'hitbig-2': 'hitbig-2.ogg',
  'hitbig-3': 'hitbig-3.ogg',
  'death-1': 'death-1.ogg',
  'death-2': 'death-2.ogg',
  'death-3': 'death-3.ogg',
};

/** Variant pools. Round-robin through these instead of detuning one sample. */
const COMBAT_VARIANTS: Record<string, string[]> = {
  attack: ['atk-1', 'atk-2', 'atk-3'],
  hit: ['hit-1', 'hit-2', 'hit-3', 'hit-4'],
  hitBig: ['hitbig-1', 'hitbig-2', 'hitbig-3'],
  death: ['death-1', 'death-2', 'death-3'],
};

/**
 * Per-cue mix trim, applied on top of the player's volume sliders.
 *
 * This is the knob to turn when a cue is too loud — NOT the source file, which is
 * loudness-matched at build time. Combat is deliberately far below the UI layer:
 * `attack` and `hit` fire on every combat exchange, hundreds of times a session,
 * and a cue you hear that often has to sit under everything else to stay tolerable.
 * Anything absent from this table plays at 1.0.
 */
const CUE_TRIM: Record<string, number> = {
  attack: 0.30,   // the most-repeated sound in the game; nearly subliminal by design
  hit: 0.55,
  // The heavy punches peak higher than the medium ones, so an equal trim would
  // have left big hits barely quieter than the old procedural set. Trimmed
  // further to keep the overall reduction real, while still landing clearly
  // above `hit` so a heavy blow still reads as heavier.
  hitBig: 0.50,
  death: 0.50,
  tapeSplice: 0.55, // fires on entry to every combat round
};

/** Rotation cursor per variant pool. */
const variantCursor: Record<string, number> = {};

/**
 * Next take from a pool. Advances by a random 1..n-1 step, so a take is never
 * played twice in a row but the order still varies between passes — a strict
 * cycle becomes its own recognisable rhythm once you have heard it a few times.
 */
function nextVariant(pool: string): string {
  const list = COMBAT_VARIANTS[pool];
  const cur = variantCursor[pool] ?? Math.floor(Math.random() * list.length);
  const step = 1 + Math.floor(Math.random() * (list.length - 1));
  const next = (cur + step) % list.length;
  variantCursor[pool] = next;
  return list[next];
}

const sampleBuffers = new Map<string, AudioBuffer>();
const samplePromises = new Map<string, Promise<AudioBuffer | null>>();

function loadSample(name: string): Promise<AudioBuffer | null> {
  const cached = sampleBuffers.get(name);
  if (cached) return Promise.resolve(cached);
  const pending = samplePromises.get(name);
  if (pending) return pending;

  const c = getCtx();
  const file = SAMPLE_FILE[name];
  if (!c || !file) return Promise.resolve(null);

  const p = fetch(`${import.meta.env.BASE_URL}sfx/${file}`)
    .then((res) => res.arrayBuffer())
    .then((buf) => c.decodeAudioData(buf))
    .then((decoded) => {
      sampleBuffers.set(name, decoded);
      return decoded;
    })
    .catch(() => null);

  samplePromises.set(name, p);
  return p;
}

/** Kick off decoding every sampled cue once audio unlocks, so first-play has no fetch latency. */
function preloadSamples() {
  Object.keys(SAMPLE_FILE).forEach((name) => loadSample(name));
}

/**
 * Play a decoded sample through the same gain pipeline + voice bookkeeping
 * as playGraph, so procedural and sampled cues share one voice budget.
 *
 * @param opts.trimKey  key into CUE_TRIM for the mix trim (defaults to `name`)
 * @param opts.gainMul  extra per-call multiplier, e.g. damage-scaled hits
 * @param opts.rate     playbackRate, i.e. pitch. Used to tint one sample per faction.
 */
function playSample(
  name: string,
  key: string | null,
  durationMs: number,
  opts: { trimKey?: string; gainMul?: number; rate?: number } = {},
) {
  if (key) {
    const min = THROTTLE_MS[key] ?? 0;
    if (min > 0) {
      const now = performance.now();
      if ((lastFireByKey[key] ?? 0) + min > now) return;
      lastFireByKey[key] = now;
    }
    if (voiceCount >= VOICE_CAP) return;
  }

  const c = getCtx();
  if (!c || !masterGain) return;

  const trim = CUE_TRIM[opts.trimKey ?? name] ?? 1;
  loadSample(name).then((buffer) => {
    if (!buffer || !ctx || !masterGain) return;
    const gain = effectiveSfxGain() * trim * (opts.gainMul ?? 1);
    if (gain <= 0.001) return;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    // Light pitch jitter on top of variant rotation. Kept small — the variants do
    // the heavy lifting now, and anything wider starts to sound like a broken tape.
    src.playbackRate.value = (opts.rate ?? 1) * jitter(1, 0.025);
    const dyn = ctx.createGain();
    dyn.gain.value = Math.max(0.01, jitter(gain, 0.08));
    src.connect(dyn).connect(masterGain);
    src.start();
  });

  voiceCount++;
  window.setTimeout(() => {
    voiceCount = Math.max(0, voiceCount - 1);
  }, durationMs + 80);
}

// ---------------------------------------------------------------------------
// Named sounds
// ---------------------------------------------------------------------------

export const sfx = {
  /** Light UI tap. Sampled — Kenney Interface Sounds. */
  click() {
    playSample('click', null, 110);
  },

  /** Disallowed action. Sampled — Kenney Interface Sounds. */
  error() {
    playSample('error', null, 170);
  },

  /** Confirm chime. Sampled — Kenney Interface Sounds. */
  success() {
    playSample('success', null, 290);
  },

  /** Card buy — chip laid down. Sampled — Kenney Casino Audio. */
  buy() {
    playSample('buy', null, 180);
  },

  /** Shop reroll — card fan. Sampled — Kenney Casino Audio. */
  refresh() {
    playSample('refresh', null, 740);
  },

  /** Tavern tier-up. Sampled — Kenney Digital Audio. */
  tierUp() {
    playSample('tierUp', null, 550);
  },

  /** Hero power activate. Sampled — Kenney Digital Audio. */
  heroPower() {
    playSample('heroPower', null, 1210);
  },

  /**
   * Attack swing — a cloth swish, rotated across three takes.
   *
   * Trimmed hardest of any cue (CUE_TRIM 0.30) because it is the most frequent
   * sound in the product: one per attacking unit per exchange. It should register
   * as movement and almost nothing more. If combat still feels noisy, this is the
   * first number to pull down, or drop the cue entirely and let `hit` carry the beat.
   */
  attack() {
    playSample(nextVariant('attack'), 'attack', 210, { trimKey: 'attack' });
  },

  /**
   * Hit impact — a body punch. Two sample families rather than one scaled sound:
   * normal exchanges get a mid-weight punch, heavy blows get a longer, lower one,
   * so damage reads as a change of *character* and not just volume.
   *
   * `amount` is the damage dealt; it also nudges gain slightly within the family.
   */
  hit(amount: number = 4) {
    const big = Math.min(Math.max(amount, 1), 20);
    const heavy = big >= 8;
    const pool = heavy ? 'hitBig' : 'hit';
    // Gentle gain ramp inside the family: 0.9 at the low end, 1.05 at the top.
    const gainMul = 0.9 + (big / 20) * 0.15;
    playSample(nextVariant(pool), 'hit', heavy ? 380 : 300, { trimKey: pool, gainMul });
  },

  /**
   * Unit death — a body hitting the floor, pitch-tinted per faction.
   *
   * The procedural version encoded faction as timbre (cholo bass thud vs emo
   * dissonant pluck). One sample family plus a playbackRate offset keeps that
   * intent — cholo drops lower and slower, emo lands higher and tighter — without
   * three separate sample sets.
   */
  death(faction: 'cholo' | 'emo' | 'neutral' = 'neutral') {
    const rate = faction === 'cholo' ? 0.88 : faction === 'emo' ? 1.14 : 1.0;
    playSample(nextVariant('death'), 'death', 480, { trimKey: 'death', rate });
  },

  /** Triple formed — pack opening. Sampled — Kenney Casino Audio. */
  triple() {
    playSample('triple', null, 940);
  },

  /** Combat win banner. Sampled — Kenney Music Jingles (Sax kit). */
  victory() {
    playSample('victory', null, 410);
  },

  /** Combat loss. Sampled — Kenney Digital Audio. */
  defeat() {
    playSample('defeat', null, 800);
  },

  /**
   * Tape transport spinning up — the Welcome → Menu transition.
   *
   * Procedural rather than sampled because it has to UNDERSCORE a 1.5s
   * sequence, not punctuate a moment: bandpassed noise is the reel whirr,
   * rising as the capstan reaches speed, over a low sine that carries the
   * motor. Passed a null throttle key so the voice cap can't drop it — this
   * is a deliberate, once-per-session cue, and the transition store already
   * guarantees it can't double-fire.
   */
  tapeTransport() {
    playGraph(null, 1300, (c, dest, t0) => {
      const dur = 1.28;

      // Reel whirr — noise through a bandpass that climbs as the reels spin
      // up, then eases back as the transport settles into speed.
      const src = noiseBuffer(c, dur);
      const bp = c.createBiquadFilter();
      bp.type = 'bandpass';
      bp.Q.value = 4.5;
      bp.frequency.setValueAtTime(420, t0);
      bp.frequency.exponentialRampToValueAtTime(2200, t0 + dur * 0.45);
      bp.frequency.exponentialRampToValueAtTime(1500, t0 + dur);
      const whirr = envelope(
        c,
        { attack: 0.14, decay: 0.2, sustain: 0.75, release: 0.34, peak: 0.3 },
        t0,
        dur,
      );
      src.start(t0);
      src.stop(t0 + dur);
      src.connect(bp).connect(whirr).connect(dest);

      // Motor — low sine climbing under the whirr so the spin-up reads as
      // torque rather than just noise getting brighter.
      const motor = osc(c, 'sine', { from: 52, to: 124, curve: 'exp' }, t0, dur);
      const motorEnv = envelope(
        c,
        { attack: 0.1, decay: 0.25, sustain: 0.6, release: 0.4, peak: 0.22 },
        t0,
        dur,
      );
      motor.connect(motorEnv).connect(dest);
    });
  },

  /**
   * Transport locking — tape seated, mechanism stops. Fires on the impact
   * frame of the transition, paired with a haptic.
   */
  tapeLock() {
    playGraph(null, 220, (c, dest, t0) => {
      const dur = 0.19;

      // Mechanical clunk — fast lowpass sweep down on noise.
      const src = noiseBuffer(c, dur);
      const lp = c.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(2600, t0);
      lp.frequency.exponentialRampToValueAtTime(380, t0 + dur);
      lp.Q.value = 1.1;
      const noiseEnv = envelope(
        c,
        { attack: 0.002, decay: 0.04, sustain: 0.2, release: 0.09, peak: 0.42 },
        t0,
        dur,
      );
      src.start(t0);
      src.stop(t0 + dur);
      src.connect(lp).connect(noiseEnv).connect(dest);

      // Body — short low triangle so the clunk has mass on phone speakers.
      const body = osc(c, 'triangle', { from: 190, to: 68, curve: 'exp' }, t0, dur * 0.8);
      const bodyEnv = envelope(
        c,
        { attack: 0.003, decay: 0.03, sustain: 0.25, release: 0.07, peak: 0.34 },
        t0,
        dur * 0.8,
      );
      body.connect(bodyEnv).connect(dest);
    });
  },

  /**
   * Two takes slamming together — the impact frame of the tavern → combat
   * splice. Harder and brighter than `tapeLock`, which is a mechanism coming to
   * rest; this is two things colliding.
   *
   * Fires every round, so it is deliberately short: a long tail would become
   * the most tiring sound in the game by round eight.
   */
  tapeSplice() {
    playGraph(null, 260, (c, dest, t0) => {
      // Trimmed via CUE_TRIM.tapeSplice — this lands on entry to EVERY combat
      // round, so it is part of the battle-fatigue budget, not a one-off flourish.
      const dur = 0.22;

      // Transient — bandpassed noise with a fast downward sweep. The high start
      // gives the "clack" of two edges meeting.
      const src = noiseBuffer(c, dur);
      const bp = c.createBiquadFilter();
      bp.type = 'bandpass';
      bp.Q.value = 0.9;
      bp.frequency.setValueAtTime(4200, t0);
      bp.frequency.exponentialRampToValueAtTime(700, t0 + dur * 0.7);
      const crack = envelope(
        c,
        { attack: 0.001, decay: 0.03, sustain: 0.18, release: 0.1, peak: 0.5 },
        t0,
        dur,
      );
      src.start(t0);
      src.stop(t0 + dur);
      src.connect(bp).connect(crack).connect(dest);

      // Sub — the weight behind the collision. Short enough not to boom.
      const sub = osc(c, 'sine', { from: 150, to: 48, curve: 'exp' }, t0, dur * 0.65);
      const subEnv = envelope(
        c,
        { attack: 0.002, decay: 0.035, sustain: 0.22, release: 0.06, peak: 0.44 },
        t0,
        dur * 0.65,
      );
      sub.connect(subEnv).connect(dest);
    }, 'tapeSplice');
  },
};

export type SfxName = keyof typeof sfx;
